import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';
import * as React from 'react';

/** Which of the two independent fetches failed — drives the scoped message `AppMain` renders. */
export type StartupErrorScope = 'organizations' | 'facilities';

export type StartupError = {
	readonly scope: StartupErrorScope;
	/** The HTTP status of the failed request, `null` when it never reached the server. */
	readonly status: number | null;
};

export type CurrentOrganizationContextValue = {
	/** `null` before resolution, whenever the caller is assigned to no organization at all, or
	 * whenever `startupError` is set (organizations and facilities are fetched together; a failure on
	 * either one leaves the selection effect from running, see `areOrganizationsConfirmed` /
	 * `areFacilitiesConfirmed` below). */
	readonly currentOrganization: ComposedOrganization | null;
	/** Every organization the caller is assigned to, each carrying its facilities, sorted by name at
	 * both levels — feeds the header switcher's grouping. Built client-side from the two independent
	 * fetches below (`composeAssignedOrganizations`), not from a single server payload. */
	readonly organizations: readonly ComposedOrganization[];
	/** `true` once both fetches (or the no-account short-circuit) have settled — succeeded or failed. */
	readonly isResolved: boolean;
	/**
	 * `true` only once the organizations fetch has actually answered with an empty list — as opposed to
	 * `organizations` being empty because there is no account to query yet, or because either fetch
	 * failed. Those are synthetic resolutions, not a confirmed answer, and consumers
	 * (`CurrentFacilityProvider`, `AppMain`) must not treat them as a green light to purge what is
	 * persisted or to claim the caller has no organization: an error is not a zero.
	 */
	readonly hasConfirmedNoOrganizations: boolean;
	/** `true` once the organizations fetch has succeeded — `false` while in flight, on failure, and
	 * when there is no account to query. A confirmation, never a "zero": distinguishing a settled
	 * failure from an unsettled request is exactly what the purge logic downstream needs. */
	readonly areOrganizationsConfirmed: boolean;
	/** Same as `areOrganizationsConfirmed`, for the facilities fetch. */
	readonly areFacilitiesConfirmed: boolean;
	/** Set once either fetch has failed — carries which one (`scope`) and its HTTP status, so `AppMain`
	 * can tell a permission failure (`403`) from any other error. `organizations` takes priority when
	 * both have failed, since `CurrentFacilityProvider`'s resolution depends on organizations first.
	 * `null` while in flight or once both have succeeded. */
	readonly startupError: StartupError | null;
	/** Persists the choice and updates `currentOrganization`. */
	readonly selectOrganization: (organizationId: number) => void;
};

const CurrentOrganizationContext = React.createContext<CurrentOrganizationContextValue | null>(null);

export default CurrentOrganizationContext;
